import React from 'react';
import { UxGenieUIEngine } from './UxGenieUIEngine';
import { UxGenieFormEngine } from './UxGenieFormEngine';
import type { GenieOutput, GenieRenderingEngineProps } from './types';

// Normalize tool name: 'ux_genie[edit]' → 'ux_genie'
const normalizeToolUsed = (toolUsed?: string | null): string =>
  toolUsed?.split('[')[0]?.trim() ?? '';

// Maps tool names to the engine they require.
// Checked against the raw (un-normalized) tool name first, then the normalized
// name, so bracket-qualified tools like 'ux_genie[form]' can be routed
// independently from their base tool 'ux_genie'.
// Export so callers can register new tools without modifying this file.
export const TOOL_ENGINE_MAP: Record<string, 'ui' | 'form'> = {
  'ux_genie[form]': 'form',  // Genie claims / structured-form tool
  ux_genie: 'ui',
  ux_genie_edit: 'ui',
};

// Maps category strings to the engine they require.
// Export so callers can register new categories without modifying this file.
export const CATEGORY_ENGINE_MAP: Record<string, 'ui' | 'form'> = {};

// Resolves the correct engine type for a given output + tool/category context.
// Resolution order:
//   1. Raw tool name match (catches 'ux_genie[form]' before normalization strips brackets)
//   2. Normalized tool name match (strips '[...]' suffix)
//   3. Explicit category mapping (CATEGORY_ENGINE_MAP)
//   4. Data-driven fallback: formConfig present → form; absent → ui
export const resolveEngine = (
  output: GenieOutput,
  toolUsed?: string | null,
  category?: string
): 'ui' | 'form' => {
  // Check raw tool name first so bracket qualifiers like '[form]' are honoured
  if (toolUsed && TOOL_ENGINE_MAP[toolUsed] !== undefined) {
    return TOOL_ENGINE_MAP[toolUsed];
  }
  const normalizedTool = normalizeToolUsed(toolUsed);
  if (normalizedTool && TOOL_ENGINE_MAP[normalizedTool] !== undefined) {
    return TOOL_ENGINE_MAP[normalizedTool];
  }
  if (category && CATEGORY_ENGINE_MAP[category] !== undefined) {
    return CATEGORY_ENGINE_MAP[category];
  }
  return output.formConfig ? 'form' : 'ui';
};

// Smart router that picks the correct rendering engine based on the API
// response output and the tool/category context. Both engines are standalone-
// safe so this component can be used anywhere in the app (chat bubbles, preview
// panels, standalone pages) by supplying the appropriate hostFunctions.
export function GenieRenderingEngine({
  output,
  toolUsed,
  category,
  hostFunctions = {},
  data,
}: GenieRenderingEngineProps) {
  const engineType = resolveEngine(output, toolUsed, category);

  if (engineType === 'form' && output.formConfig) {
    return (
      <UxGenieFormEngine
        component={output.component}
        formConfig={output.formConfig}
        hostFunctions={hostFunctions}
        data={data}
      />
    );
  }

  return <UxGenieUIEngine component={output.component} hostFunctions={hostFunctions} data={data} />;
}

export default GenieRenderingEngine;
